import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TopUpModal from '../components/TopUpModal';

export default function PackagePurchase() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('app');

  const [packages, setPackages] = useState([]);
  const [packageStatus, setPackageStatus] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selections, setSelections] = useState({});
  const [selectedSeries, setSelectedSeries] = useState('Y');
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [topUpStatusMessage, setTopUpStatusMessage] = useState(null);
  const [orderStatusMessage, setOrderStatusMessage] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [authLoading, user, navigate]);

  // Handle return from a Credit Points top-up's Stripe Checkout: poll the
  // top-up's own status. Stripe top-ups auto-credit on payment success
  // (migration 195) -- the webhook moves status straight to "approved",
  // never "pending_review", so refetch the balance once that happens.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topupResult = params.get('topup');
    const topupId = params.get('topup_id');

    if (topupResult === 'cancelled') {
      setTopUpStatusMessage({ type: 'info', text: 'Top-up payment was cancelled.' });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (topupResult === 'success' && topupId) {
      setTopUpStatusMessage({ type: 'pending', text: "Payment received. We're confirming it..." });
      window.history.replaceState({}, '', window.location.pathname);

      const token = localStorage.getItem('token');
      let attempts = 0;
      const maxAttempts = 15; // ~30 seconds at 2s intervals

      const poll = async () => {
        attempts += 1;
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-topup-list`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            const topup = (data.topups || []).find((t) => t.id === topupId);
            if (topup?.status === 'approved') {
              setTopUpStatusMessage({ type: 'success', text: `Payment received — ${topup.amount_cp} Credit Points added to your balance.` });
              fetchData();
              return;
            }
            if (topup?.status === 'rejected') {
              setTopUpStatusMessage({ type: 'error', text: 'This top-up could not be processed. Please try again.' });
              return;
            }
          }
        } catch (err) {
          console.error('Top-up status poll failed:', err);
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setTopUpStatusMessage({ type: 'info', text: 'Still processing — check back shortly.' });
        }
      };

      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle return from a package purchase's Stripe Checkout (both the
  // established self-service flow here on /packages, and Signup Wizard
  // purchases from /signup or /signup-internal, which land here too).
  // Self-service orders auto-confirm almost immediately via the webhook;
  // Signup Wizard orders deliberately stay 'pending' until an admin
  // manually confirms (per explicit requirement), so this shows a
  // different message depending on which status the order actually
  // settles into, rather than assuming either outcome.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeResult = params.get('stripe');
    const orderId = params.get('order_id');

    if (stripeResult === 'cancelled') {
      setOrderStatusMessage({ type: 'info', text: 'Payment was cancelled.' });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    if (stripeResult === 'success' && orderId) {
      setOrderStatusMessage({ type: 'pending', text: "Payment received. We're confirming it..." });
      window.history.replaceState({}, '', window.location.pathname);

      const token = localStorage.getItem('token');
      let attempts = 0;
      const maxAttempts = 15; // ~30 seconds at 2s intervals

      const poll = async () => {
        attempts += 1;
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/order-status/${orderId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'confirmed') {
              setOrderStatusMessage({ type: 'success', text: 'Payment received — your package is now active!' });
              fetchData();
              return;
            }
            if (data.status === 'rejected' || data.status === 'cancelled') {
              setOrderStatusMessage({ type: 'error', text: 'This order could not be processed. Please contact support.' });
              return;
            }
            // Still 'pending' after several checks -- this is expected
            // for Signup Wizard orders (never auto-confirms), so stop
            // polling and show the thank-you note rather than looping
            // until timeout.
            if (attempts >= 5) {
              setOrderStatusMessage({
                type: 'success',
                text: 'Thank you for your purchase. We will contact you as soon as possible when your purchase is confirmed.',
              });
              return;
            }
          }
        } catch (err) {
          console.error('Order status poll failed:', err);
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setOrderStatusMessage({
            type: 'success',
            text: 'Thank you for your purchase. We will contact you as soon as possible when your purchase is confirmed.',
          });
        }
      };

      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const packagesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/packages`);
      if (!packagesRes.ok) throw new Error('Failed to fetch packages');
      const packagesData = await packagesRes.json();
      setPackages(packagesData.packages || []);

      const statusRes = await fetch(`${process.env.REACT_APP_API_URL}/api/user/package-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!statusRes.ok) throw new Error('Failed to fetch package status');
      const statusData = await statusRes.json();
      setPackageStatus(statusData);

      const balanceRes = await fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-balance`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!balanceRes.ok) throw new Error('Failed to fetch credit balance');
      const balanceData = await balanceRes.json();
      setCreditBalance(balanceData.credit_point_balance || 0);
    } catch (err) {
      console.error('Failed to fetch package data:', err);
      setError('Failed to load packages. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setPurchaseError(null);
    setSuccessMessage(null);
    setPurchasing(true);

    try {
      const token = localStorage.getItem('token');
      const selected_item_ids = selections[packageId] ? Object.values(selections[packageId]) : [];

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/purchase-package`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: packageId,
          selected_item_ids: selected_item_ids
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'insufficient_credits') {
          setPurchaseError(t('packagePurchase.insufficientError'));
        } else if (data.error === 'package_already_active') {
          setPurchaseError(t('packagePurchase.alreadyActive'));
        } else if (data.error === 'downgrade_not_allowed') {
          setPurchaseError('Cannot downgrade to a lower or equal-priced package');
        } else if (data.error === 'selection_required') {
          setPurchaseError(t('packagePurchase.selectionRequired'));
        } else if (data.error === 'invalid_selection') {
          setPurchaseError(t('packagePurchase.invalidSelection'));
        } else if (data.error === 'invalid_package_config') {
          setPurchaseError(t('packagePurchase.invalidPackageConfig'));
        } else {
          setPurchaseError(data.message || t('packagePurchase.failedToSubmit'));
        }
        return;
      }

      const pkg = packages.find(p => p.id === packageId);
      setSuccessMessage(t('packagePurchase.purchaseSuccess', { price: pkg.price, balance: data.new_balance }));
      setCreditBalance(data.new_balance);
      await fetchData();
    } catch (err) {
      console.error('Purchase error:', err);
      setPurchaseError(t('packagePurchase.failedToSubmit'));
    } finally {
      setPurchasing(false);
    }
  };

  const hasAllSelectionsForPackage = (pkg) => {
    if (!pkg.items || pkg.items.length === 0) return true;
    for (const item of pkg.items) {
      if (item.options.length > 1) {
        if (!selections[pkg.id]?.[item.slot_number]) return false;
      }
    }
    return true;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  const isSuperadmin = user?.role === 'superadmin';
  const isVerified = packageStatus?.verified && !isSuperadmin;
  const currentPackage = packageStatus?.package;
  
  const SERIES_ORDER = ['Y', 'L', 'M'];
  const availableSeries = [...new Set(packages.map(p => p.series || 'Y'))]
    .sort((a, b) => {
      const ia = SERIES_ORDER.indexOf(a), ib = SERIES_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

  const SERIES_LABELS = { Y: 'Y-Series', L: 'L-Series', M: 'M-Series' };

  // Ensure all packages have series (default to 'Y' for legacy packages)
  const packagesWithSeries = packages.map(pkg => ({
    ...pkg,
    series: pkg.series || 'Y',
    description_en: pkg.description_en || pkg.name_en || '',
    description_cn: pkg.description_cn || pkg.name_cn || ''
  }));

  const filteredPackages = packagesWithSeries.filter(pkg => pkg.series === selectedSeries);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <style>{`
        /* Mobile-first CSS approach */
        
        /* 8pt spacing scale */
        .spacing-xs { margin: 4px; }
        .spacing-sm { margin: 8px; }
        .spacing-md { margin: 16px; }
        .spacing-lg { margin: 24px; }
        .spacing-xl { margin: 32px; }
        
        /* Touch targets - minimum 44x44px */
        button { min-height: 48px; }
        
        /* Smooth scrolling for category pills */
        .category-scroll {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          padding-left: 16px;
          padding-right: 16px;
          margin: 16px 0;
        }
        
        .category-scroll::-webkit-scrollbar {
          height: 4px;
        }
        
        .category-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 2px;
        }
        
        .category-pill {
          flex-shrink: 0;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          transition: all 0.3s ease;
          border: 2px solid #e5e7eb;
          background: white;
          color: #666;
        }
        
        .category-pill.active {
          background: #2563eb;
          border-color: #2563eb;
          color: white;
        }
        
        .category-pill:active {
          transform: scale(0.98);
        }
        
        /* Product card animations */
        .product-card {
          animation: fadeIn 0.3s ease-in;
        }

        /* CTA buttons (Buy with Credits / Top Up Credit Points) -- Wellis colors,
           unconditional so it applies at every viewport width, not just mobile */
        .cta-button {
          height: 48px;
          background: linear-gradient(135deg, #0F2915 0%, #1a3a2a 100%);
          color: #D4AF37;
          border: 2px solid #D4AF37;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 8px;
          box-shadow: 0 4px 12px rgba(15, 41, 21, 0.3);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .cta-button:active:not(:disabled) {
          background: linear-gradient(135deg, #1a3a2a 0%, #0F2915 100%);
          transform: scale(0.98);
          box-shadow: 0 4px 16px rgba(212, 175, 55, 0.35);
        }

        .cta-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: none;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Responsive grid */
        @media (max-width: 430px) {
          .products-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 16px;
          }
          
          .header-sticky {
            position: sticky;
            top: 0;
            z-index: 20;
            background: white;
            border-bottom: 1px solid #e5e7eb;
            height: 56px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 16px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
          
          .hero-mobile {
            height: 240px;
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            padding: 24px 16px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            color: white;
          }
          
          .hero-title {
            font-size: 28px;
            font-weight: 700;
            line-height: 1.2;
            margin-bottom: 8px;
          }
          
          .hero-subtitle {
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
            margin-bottom: 16px;
            opacity: 0.95;
          }
          
          .product-card {
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            transition: all 0.3s ease;
          }
          
          .product-card:active {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          }
          
          .product-image {
            width: 100%;
            aspect-ratio: 1 / 1;
            background: #f3f4f6;
            object-fit: contain;
            padding: 16px;
          }
          
          .product-content {
            padding: 16px;
          }
          
          .product-code {
            font-size: 12px;
            font-family: monospace;
            color: #999;
            margin-bottom: 4px;
          }
          
          .product-name {
            font-size: 20px;
            font-weight: 600;
            color: #111;
            margin-bottom: 6px;
            line-height: 1.2;
          }
          
          .product-description {
            font-size: 14px;
            color: #666;
            margin-bottom: 12px;
            line-height: 1.4;
          }
          
          .price-badge {
            background: #f0f4ff;
            border-left: 4px solid #2563eb;
            padding: 8px 12px;
            margin-bottom: 12px;
            border-radius: 4px;
          }
          
          .price-value {
            font-size: 28px;
            font-weight: 700;
            color: #2563eb;
            line-height: 1;
          }
          
          .price-label {
            font-size: 12px;
            color: #666;
            margin-top: 4px;
          }
          
          .bonus-badge {
            background: #ecfdf5;
            border-left: 4px solid #10b981;
            padding: 8px 12px;
            margin-bottom: 12px;
            border-radius: 4px;
          }
          
          .bonus-title {
            font-size: 12px;
            font-weight: 600;
            color: #059669;
            margin-bottom: 4px;
          }
          
          .bonus-item {
            font-size: 14px;
            color: #047857;
            margin: 4px 0;
          }
          
          .cta-button {
            width: 100%;
          }

          .status-badge {
            background: white;
            border-radius: 12px;
            padding: 16px;
            margin: 16px;
            border-left: 4px solid #10b981;
          }
        }
        
        /* Tablet and up */
        @media (min-width: 431px) {
          .products-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 24px;
            padding: 32px;
          }
          
          .category-scroll {
            flex-wrap: wrap;
            padding-left: 32px;
            padding-right: 32px;
            margin: 24px 0;
          }
          
          .cta-button {
            width: 100%;
          }
        }
      `}</style>

      {/* Sticky Mobile Header */}
      <div className="header-sticky">
        <h1 className="text-xl font-bold text-gray-900">Wellis</h1>
        {!isSuperadmin && (
          <button
            onClick={() => setTopUpModalOpen(true)}
            className="px-3 py-1.5 text-sm font-medium rounded-md"
            style={{ backgroundColor: '#0F2915', color: '#D4AF37', border: '1px solid #D4AF37' }}
          >
            Top Up Credit Points
          </button>
        )}
      </div>

      {/* Top-level Top Up entry point for non-mobile / non-sticky-header contexts */}
      {!isSuperadmin && (
        <div className="hidden md:flex justify-end px-8 pt-4">
          <button
            onClick={() => setTopUpModalOpen(true)}
            className="px-4 py-2 text-sm font-medium rounded-md"
            style={{ backgroundColor: '#0F2915', color: '#D4AF37', border: '1px solid #D4AF37' }}
          >
            Top Up Credit Points
          </button>
        </div>
      )}

      <TopUpModal
        isOpen={topUpModalOpen}
        onClose={() => setTopUpModalOpen(false)}
        onSuccess={fetchData}
      />

      {/* Series Info */}
      {!isVerified && (
        <div className="hero-mobile">
          <h2 className="hero-title">Upgrade Your Membership</h2>
          <p className="hero-subtitle">Choose a package to activate your membership and unlock benefits</p>
        </div>
      )}

      {/* Series Tabs / Category Pills */}
      {!isVerified && (
        <div className="category-scroll">
          {availableSeries.map((series) => (
            <button
              key={series}
              type="button"
              onClick={() => setSelectedSeries(series)}
              className={`category-pill ${selectedSeries === series ? 'active' : ''}`}
            >
              {SERIES_LABELS[series] || `${series}-Series`}
            </button>
          ))}
        </div>
      )}

      {/* Status Badge (if verified) */}
      {isVerified && (
        <div className="status-badge">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">✓</span>
            <h2 className="text-lg font-bold text-green-700">Membership Verified</h2>
          </div>
          <p className="text-sm text-gray-700">
            You have been verified as a member under the <strong>{i18n.language === 'zh' ? currentPackage?.name_cn : currentPackage?.name_en}</strong>
          </p>
        </div>
      )}

      {/* Upgrade Section (for verified members) */}
      {isVerified && (() => {
        const upgradePackages = packagesWithSeries.filter(pkg => pkg.price > currentPackage?.price && pkg.series === selectedSeries);

        if (upgradePackages.length > 0) {
          return (
            <>
              <div className="hero-mobile">
                <h2 className="hero-title">{i18n.language === 'zh' ? '升级您的会员资格' : 'Upgrade Your Membership'}</h2>
                <p className="hero-subtitle">{i18n.language === 'zh' ? '升级到更高等级的配套以获得更多优势' : 'Upgrade to a higher tier for additional benefits'}</p>
              </div>

              <div className="category-scroll">
                {availableSeries.map((series) => (
                  <button
                    key={series}
                    type="button"
                    onClick={() => setSelectedSeries(series)}
                    className={`category-pill ${selectedSeries === series ? 'active' : ''}`}
                  >
                    {SERIES_LABELS[series] || `${series}-Series`}
                  </button>
                ))}
              </div>

              <div className="products-grid">
                {upgradePackages.map((pkg) => {
                  const canAfford = creditBalance >= pkg.price;

                  return (
                    <div key={pkg.id} className="product-card">
                      {/* Product Image */}
                      <div className="relative">
                        {pkg.image_url ? (
                          <img
                            src={(i18n.language === 'zh' && pkg.image_url_cn) ? pkg.image_url_cn : pkg.image_url}
                            alt={pkg.code}
                            className="product-image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="product-image flex items-center justify-center">
                            <span className="text-gray-400">{pkg.code}</span>
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="product-content">
                        <p className="product-code">{pkg.code}</p>
                        <h3 className="product-name">
                          {i18n.language === 'zh' ? pkg.name_cn : pkg.name_en}
                        </h3>
                        <p className="product-description">
                          {i18n.language === 'zh' ? pkg.description_cn : pkg.description_en}
                        </p>

                        {/* Price Badge */}
                        <div className="price-badge">
                          <div className="price-value">
                            {pkg.price.toLocaleString()}
                          </div>
                          <div className="price-label">
                            {i18n.language === 'zh' ? '认购积分 (SP)' : 'Credit Points (SP)'}
                          </div>
                        </div>

                        {/* Welcome Bonus */}
                        {(pkg.cash_amount > 0 || pkg.product_credit > 0) && (
                          <div className="bonus-badge">
                            <div className="bonus-title">
                              {i18n.language === 'zh' ? '欢迎奖励' : 'Welcome Bonus'}
                            </div>
                            {pkg.cash_amount > 0 && (
                              <div className="bonus-item">
                                {pkg.cash_amount} {i18n.language === 'zh' ? '现金积分 (CP)' : 'Cash Points (CP)'}
                              </div>
                            )}
                            {pkg.product_credit > 0 && (
                              <div className="bonus-item">
                                {pkg.product_credit} {i18n.language === 'zh' ? '产品积分 (PP)' : 'Product Points (PP)'}
                              </div>
                            )}
                          </div>
                        )}

                        {/* CTA Buttons */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handlePurchase(pkg.id)}
                            disabled={!canAfford || purchasing || !hasAllSelectionsForPackage(pkg)}
                            className="cta-button"
                            title={!canAfford ? 'Insufficient Credits' : !hasAllSelectionsForPackage(pkg) ? 'Complete selections' : ''}
                          >
                            {!canAfford ? `Need ${(pkg.price - creditBalance).toFixed(0)} more` : !hasAllSelectionsForPackage(pkg) ? 'Select items' : purchasing ? 'Processing...' : 'Upgrade with Credits'}
                          </button>
                          {!canAfford && (
                            <button
                              onClick={() => setTopUpModalOpen(true)}
                              className="cta-button"
                            >
                              Top Up Credit Points
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        }
        return null;
      })()}

      {/* Error/Success Messages */}
      {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 rounded text-red-700 text-sm">{error}</div>}
      {purchaseError && <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-4 rounded text-red-700 text-sm">{purchaseError}</div>}
      {successMessage && <div className="bg-green-50 border-l-4 border-green-500 p-4 mx-4 rounded text-green-700 text-sm">{successMessage}</div>}
      {topUpStatusMessage && (
        <div className={`p-4 mx-4 rounded text-sm border-l-4 ${
          topUpStatusMessage.type === 'success' ? 'bg-green-50 border-green-500 text-green-700'
          : topUpStatusMessage.type === 'error' ? 'bg-red-50 border-red-500 text-red-700'
          : 'bg-blue-50 border-blue-500 text-blue-700'
        }`}>
          {topUpStatusMessage.text}
        </div>
      )}
      {orderStatusMessage && (
        <div className={`p-4 mx-4 rounded text-sm border-l-4 ${
          orderStatusMessage.type === 'success' ? 'bg-green-50 border-green-500 text-green-700'
          : orderStatusMessage.type === 'error' ? 'bg-red-50 border-red-500 text-red-700'
          : 'bg-blue-50 border-blue-500 text-blue-700'
        }`}>
          {orderStatusMessage.text}
        </div>
      )}

      {/* Packages Grid */}
      {!isVerified && (
        <div className="products-grid">
          {filteredPackages.length > 0 ? (
            filteredPackages.map((pkg) => {
              const canAfford = creditBalance >= pkg.price;
              const isCurrentPackage = currentPackage?.id === pkg.id;

              return (
                <div key={pkg.id} className="product-card">
                  {/* Product Image */}
                  <div className="relative">
                    {pkg.image_url ? (
                      <img
                        src={(i18n.language === 'zh' && pkg.image_url_cn) ? pkg.image_url_cn : pkg.image_url}
                        alt={pkg.code}
                        className="product-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="product-image flex items-center justify-center">
                        <span className="text-gray-400">{pkg.code}</span>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="product-content">
                    <p className="product-code">{pkg.code}</p>
                    <h3 className="product-name">
                      {i18n.language === 'zh' ? pkg.name_cn : pkg.name_en}
                    </h3>
                    <p className="product-description">
                      {i18n.language === 'zh' ? pkg.description_cn : pkg.description_en}
                    </p>

                    {/* Price Badge */}
                    <div className="price-badge">
                      <div className="price-value">
                        {pkg.price.toLocaleString()}
                      </div>
                      <div className="price-label">
                        {i18n.language === 'zh' ? '认购积分 (SP)' : 'Credit Points (SP)'}
                      </div>
                    </div>

                    {/* Welcome Bonus */}
                    {(pkg.cash_amount > 0 || pkg.product_credit > 0) && (
                      <div className="bonus-badge">
                        <div className="bonus-title">
                          {i18n.language === 'zh' ? '欢迎奖励' : 'Welcome Bonus'}
                        </div>
                        {pkg.cash_amount > 0 && (
                          <div className="bonus-item">
                            {pkg.cash_amount} {i18n.language === 'zh' ? '现金积分 (CP)' : 'Cash Points (CP)'}
                          </div>
                        )}
                        {pkg.product_credit > 0 && (
                          <div className="bonus-item">
                            {pkg.product_credit} {i18n.language === 'zh' ? '产品积分 (PP)' : 'Product Points (PP)'}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Item Selection */}
                    {pkg.items && pkg.items.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {pkg.items.map((item, idx) => {
                          const hasChoice = (item.options?.length || 0) > 1;

                          if (!hasChoice) {
                            return (
                              <p key={idx} className="text-sm text-gray-600">
                                {i18n.language === 'zh' ? item.options?.[0]?.option_label_cn : item.options?.[0]?.option_label_en}
                              </p>
                            );
                          }

                          return (
                            <div key={idx} className="border rounded-lg p-3">
                              <p className="text-sm font-medium mb-2">
                                {i18n.language === 'zh' ? '请选择一项' : 'Please choose one'}
                              </p>
                              {item.options.map((opt) => (
                                <label key={opt.id} className="flex items-center gap-2 cursor-pointer mb-2 last:mb-0">
                                  <input
                                    type="radio"
                                    name={`slot_${pkg.id}_${item.slot_number}`}
                                    value={opt.id}
                                    checked={selections[pkg.id]?.[item.slot_number] === opt.id}
                                    onChange={() => {
                                      setSelections(prev => ({
                                        ...prev,
                                        [pkg.id]: {
                                          ...prev[pkg.id],
                                          [item.slot_number]: opt.id
                                        }
                                      }));
                                    }}
                                  />
                                  <span className="text-sm">
                                    {i18n.language === 'zh' ? opt.option_label_cn : opt.option_label_en}
                                  </span>
                                </label>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* CTA Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePurchase(pkg.id)}
                        disabled={isSuperadmin || !canAfford || isCurrentPackage || purchasing || !hasAllSelectionsForPackage(pkg)}
                        className="cta-button"
                        title={isSuperadmin ? 'View Only' : isCurrentPackage ? 'Currently Active' : !canAfford ? 'Insufficient Credits' : !hasAllSelectionsForPackage(pkg) ? 'Complete selections' : ''}
                      >
                        {isSuperadmin ? 'View Only' : isCurrentPackage ? 'Active' : !canAfford ? `Need ${(pkg.price - creditBalance).toFixed(0)} more` : !hasAllSelectionsForPackage(pkg) ? 'Select items' : purchasing ? 'Processing...' : 'Buy with Credits'}
                      </button>
                      {!isSuperadmin && !isCurrentPackage && !canAfford && (
                        <button
                          onClick={() => setTopUpModalOpen(true)}
                          className="cta-button"
                        >
                          Top Up Credit Points
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-600 text-lg">
                {i18n.language === 'zh' ? '此系列暂无产品' : 'No products in this series'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
